import { useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface ImportChoiceDialogProps {
  fileName: string;
  sheetCount: number;
  onAdd: () => void;
  onReplace: () => void;
  onCancel: () => void;
}

/**
 * The one choice an .xlsx import needs that nothing else in the File menu
 * does. Opening a blank workbook or an example only ever replaces — there is
 * nothing else they could do. An .xlsx import is different whenever a
 * workbook is already open: "replace" and "cancel" alone would lose whatever
 * was there, so this is a real three-way choice rather than a yes/no confirm.
 */
export function ImportChoiceDialog({
  fileName,
  sheetCount,
  onAdd,
  onReplace,
  onCancel,
}: ImportChoiceDialogProps): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const plural = sheetCount === 1 ? '' : 's';

  return createPortal(
    <div
      className="gt-dialog-backdrop"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="gt-dialog" role="alertdialog" aria-modal="true" aria-label={`Import ${fileName}`}>
        <div className="gt-dialog-title">{fileName}</div>
        <div className="gt-dialog-body">
          {sheetCount} sheet{plural} — there's already a workbook open. Add {sheetCount === 1 ? 'it' : 'them'}{' '}
          alongside what's here, or replace the open workbook entirely?
        </div>
        <div className="gt-dialog-actions">
          <button className="gt-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="gt-btn" onClick={onReplace}>
            Replace workbook
          </button>
          <button className="gt-btn gt-btn-primary" onClick={onAdd} autoFocus>
            Add as new sheet{plural}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
